import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

/**
 * Integration Story Panel
 * --------------------------------------------------------------------------
 * Investor-pitch deliverable: explains in one screen how ClearEqual gets data
 * from existing HRIS systems. Reads as a clean, compact architecture story
 * rather than a marketing billboard. Designed to answer:
 *   "How do you actually get the data?"
 *   "Which systems do you support?"
 *   "What about security and EU data residency?"
 */
const IntegrationStoryPanel = () => {
  const { t } = useTranslation();

  const connectors = [
    { titleKey: 'integrationStory.sapTitle',     descKey: 'integrationStory.sapDesc',     icon: 'Server',   color: 'text-blue-600' },
    { titleKey: 'integrationStory.workdayTitle', descKey: 'integrationStory.workdayDesc', icon: 'Briefcase',color: 'text-orange-600' },
    { titleKey: 'integrationStory.oracleTitle',  descKey: 'integrationStory.oracleDesc',  icon: 'Database', color: 'text-red-600' },
    { titleKey: 'integrationStory.adpTitle',     descKey: 'integrationStory.adpDesc',     icon: 'CreditCard',color: 'text-emerald-600' },
    { titleKey: 'integrationStory.umantisTitle', descKey: 'integrationStory.umantisDesc', icon: 'Users',    color: 'text-violet-600' },
    { titleKey: 'integrationStory.mergeTitle',   descKey: 'integrationStory.mergeDesc',   icon: 'GitMerge', color: 'text-primary-solid' },
    { titleKey: 'integrationStory.csvTitle',     descKey: 'integrationStory.csvDesc',     icon: 'Upload',   color: 'text-slate-500' },
  ];

  const security = [
    'integrationStory.security1',
    'integrationStory.security2',
    'integrationStory.security3',
    'integrationStory.security4',
  ];

  const sync = [
    'integrationStory.sync1',
    'integrationStory.sync2',
    'integrationStory.sync3',
    'integrationStory.sync4',
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft mb-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-primary/10 text-primary-solid rounded-lg flex items-center justify-center shrink-0">
          <Icon name="GitBranch" size={20} />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {t('integrationStory.title')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            {t('integrationStory.subtitle')}
          </p>
        </div>
      </div>

      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mt-6 mb-3">
        {t('integrationStory.connectorsLabel')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {connectors.map((c, i) => (
          <div key={i} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border">
            <Icon name={c.icon} size={18} className={`${c.color} shrink-0 mt-0.5`} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">{t(c.titleKey)}</div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{t(c.descKey)}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-border">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Shield" size={16} className="text-primary-solid" />
            <h3 className="text-sm font-semibold text-foreground">
              {t('integrationStory.securityTitle')}
            </h3>
          </div>
          <ul className="space-y-1.5">
            {security.map((k, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                <span className="text-primary-solid mt-0.5">•</span>
                <span className="leading-relaxed">{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="RefreshCw" size={16} className="text-primary-solid" />
            <h3 className="text-sm font-semibold text-foreground">
              {t('integrationStory.syncTitle')}
            </h3>
          </div>
          <ul className="space-y-1.5">
            {sync.map((k, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                <span className="text-primary-solid mt-0.5">•</span>
                <span className="leading-relaxed">{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default IntegrationStoryPanel;
